import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import isEmpty from 'lodash/isEmpty';
import Config from './config';
import { createService } from '../https';
import * as Services from '../services';
import { SDKCache } from '../utils';

/**
 * The docs SDK class, which is the entry point to access data.
 *
 * https://tsdoc.org/pages/tags/internal/
 *
 * @public
 */
var DocsClass = /*#__PURE__*/function () {
  /**
   * Initialized configurations for SDK.
   */

  /**
   * Internal LRUCache instance for SDK
   */

  /**
   * All internal services instance supported by SDK.
   * E.g. `dmV1`, `dmV2`, `exportV1` and so on.
   *
   * Can use this to send request to specific version API directly.
   *
   *  - Docs.HttpService.dmV1.[get|put|delete|post|patch|put|sendRequest](..., config)
   */

  // /////////////////////////////////////////////////////////////////////////////////// //
  // Following services are wrapped by SDK for easy access                               //
  // Comparing to use HttpService.dmV1,                                                  //
  // the service blow encapsulated the request logic and parse response internally       //
  // /////////////////////////////////////////////////////////////////////////////////// //
  /**
   * Service to manage activities
   */

  /**
   * Service to manage user, role and company info with admin APIs
   */

  /**
   * Service to manage application level settings
   */

  /**
   * Constructor to initialize the SDK.
   *
   * @param options - The SDK options to initialize the SDK.
   */
  function DocsClass(options) {
    _classCallCheck(this, DocsClass);
    /**
     * Internal config manager to initialize SDK configurations.
     * @internal
     */
    /**
     * Internal flag to check if SDK is initialized.
     * @internal
     */
    _defineProperty(this, "_initialized", false);
    this._initialized = false;
    if (options) this.init(options);
  }

  /**
   * Initialize the sdk with options.
   *
   * @param options - The SDK options to initialize the SDK.
   */
  return _createClass(DocsClass, [{
    key: "init",
    value: function init(options) {
      if (this._initialized) return true;

      // Mark the SDK as initialized first
      this._initialized = true;
      this._config = new Config();
      // Create the global configuration objects.
      this.Config = this._config.get(options);
      this.HttpService = createService(this.Config);
      this.Cache = new SDKCache(this.Config);

      // Services provided by the SDK
      // >>> Add in alphabetical order <<<<
      this.Activities = new Services.ActivityService(this);
      this.Admin = new Services.AdminService(this);
      this.Apps = new Services.ApplicationService(this);
      this.AutoCallouts = new Services.AutoCalloutsService(this);
      this.Automation = new Services.AutomationService(this);
      this.Batch = new Services.BatchService(this);
      this.BuildingConnected = new Services.BuildingConnectedService(this);
      this.CdeSettings = new Services.CdeSettingsService(this);
      this.Classification = new Services.ClassificationService(this);
      this.CopiedProject = new Services.CopiedProjectService(this);
      this.CopyProjectWorkflows = new Services.CopyProjectWorkflowsService(this);
      this.CustomAttributes = new Services.CustomAttributesService(this);
      this.DerivativeService = new Services.DerivativeServiceService(this);
      this.Disciplines = new Services.DisciplinesService(this);
      this.Documents = new Services.DocumentService(this);
      this.DS = new Services.DSService(this);
      this.Export = new Services.ExportService(this);
      this.Folders = new Services.FolderService(this);
      this.ForgeData = new Services.ForgeDataService(this);
      this.HoldingArea = new Services.HoldingAreaService(this);
      this.HQ = new Services.HQService(this);
      this.Markup = new Services.MarkupService(this);
      this.MD = new Services.MDService(this);
      this.NamingConventions = new Services.NamingConventionsService(this);
      this.NamingStandards = new Services.NamingStandardsService(this);
      this.PackageDetail = new Services.PackageDetailService(this);
      this.PackagesList = new Services.PackagesListService(this);
      this.PackagesSettings = new Services.PackagesSettingsService(this);
      this.Projects = new Services.ProjectService(this);
      this.Report = new Services.ReportService(this);
      this.Reviews = new Services.ReviewsService(this);
      this.RevitUpgradeService = new Services.RevitUpgradeService(this);
      this.Search = new Services.SearchService(this);
      this.Share = new Services.ShareService(this);
      this.Sheets = new Services.SheetsService(this);
      this.Specifications = new Services.SpecificationsService(this);
      this.Transmittals = new Services.TransmittalsService(this);
      this.Upload = new Services.UploadService(this);
      this.Users = new Services.UserService(this);
      this.VersionSets = new Services.VersionSetsService(this);
      this.WorkflowDrafts = new Services.WorkflowDraftsService(this);
      this.Workflows = new Services.WorkflowsService(this);
      return true;
    }

    /**
     * Update SDK configuration after initialized.
     *
     * @param options - The new config options
     * @returns true if successfully updated, otherwise false.
     */
  }, {
    key: "setConfig",
    value: function setConfig(options) {
      if (isEmpty(options)) return false;
      if (!this._initialized) throw new Error('[SDK] not initialized, call init to initialize!');

      // Recreate the global configuration objects.
      this.Config = this._config.update(options);
      this.HttpService = createService(this.Config);
      this.Cache = new SDKCache(this.Config);
      return true;
    }
  }]);
}();
/**
 * Expose the SDK singleton instance and DocsClass as well
 */
var instance = new Proxy(new DocsClass(), {
  get: function get(target, prop) {
    if (target._initialized === false && !['init', 'setConfig', '_initialized'].includes(prop)) {
      throw new Error('[SDK] not initialized, call init to initialize first!');
    }
    return target[prop];
  }
});
export { DocsClass, instance as default };